import 'react-datepicker/dist/react-datepicker.css';
import React, { useContext, useState, useEffect } from 'react';
import { Dimensions, StyleSheet, Text, Pressable, View, TextInput, Switch } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import { useNavigate } from 'react-router-dom';
import RegistrationBanner from '../../../../components/session/RegistrationBanner';
import CardImageContainer from './CardImageContainer';
import ShareModal from '../../../../components/general/ShareModal';
import PhotosModal from '../../../../components/session/PhotosModal';
import { v4 as uuidv4 } from 'uuid';
import SessionTypeBanner from '../../../../components/session/SessionTypeBanner';
import SessionTypeEditModal from '../../../camp_screen/components/session/SessionTypeEditModal';
import DataOps from '../../../../data/DataOps';
import PriceEditModal from '../../../camp_screen/components/session/PriceEditModal';
import TitleEditModal from '../../../camp_screen/components/session/TitleEditModal';
import ClassSizeEditModal from '../../../camp_screen/components/session/ClassSizeEditModal';
import TimeRangeEditModal from '../../../camp_screen/components/session/TimeRangeEditModal';
import AgeRangeEditModal from '../../../camp_screen/components/session/AgeRangeEditModal';
import DateRangeEditModal from '../../../camp_screen/components/session/DateRangeEditModal';

import CancellationPolicyEditModal from '../../../camp_screen/components/session/CancellationPolicyEditModal';
import WaiverEditModal from '../../../camp_screen/components/session/WaiverEditModal';
import CoachEditModal from '../../../camp_screen/components/session/CoachEditModal';
import TimeOps from '../../../../data/TimeOps';
import NotificationOps from '../../../../data/NotificationOps';
import SessionUtils from '../../../../utility/SessionUtils';

const WIDTH = Dimensions.get('window').width * 0.166;

const SessionCard = ({ session, campSessionPage, isEditing, setSessions = null, camp = null }) => {
    const navigate = useNavigate();

    const {
        user: user,
        likedSessions: likedSessions,
        setLikedSessions: setLikedSessions,
        userCampId: userCampId,
    } = useContext(GlobalStateContext);

    const isLiked = likedSessions.has(session.sessionId);
    const [editableSession, setEditableSession] = useState(session);
    const [isHost, setIsHost] = useState(false);
    const [coachData, setCoachData] = useState<any[]>([]);
    const sessionFollowersCount =
        typeof editableSession.followers === 'number'
            ? editableSession.followers
            : Array.isArray(editableSession.sessionFollowers)
                ? editableSession.sessionFollowers.length
                : 0;

    const [showPriceModal, setShowPriceModal] = useState(false);
    const [showTitleModal, setShowTitleModal] = useState(false);
    const [showClassSizeModal, setShowClassSizeModal] = useState(false);
    const [showTimeRangeModal, setShowTimeRangeModal] = useState(false);
    const [showAgeRangeModal, setShowAgeRangeModal] = useState(false);
    const [showDateRangeModal, setShowDateRangeModal] = useState(false);

    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [showWaiverModal, setShowWaiverModal] = useState(false);
    const [isWaiverRowHovered, setIsWaiverRowHovered] = useState(false);
    const [showSessionTypeModal, setShowSessionTypeModal] = useState(false);
    const [showInstructorModal, setShowInstructorModal] = useState(false);
    const [hoveredField, setHoveredField] = useState(null);
    const [isMapRowHovered, setIsMapRowHovered] = useState(false);
    const [isCalendarRowHovered, setIsCalendarRowHovered] = useState(false);
    const [isInstructorRowHovered, setIsInstructorRowHovered] = useState(false);

    useEffect(() => {
        const fetchCoachData = async () => {
            if (!camp?.campCoaches || camp.campCoaches.length === 0) {
                setCoachData([]);
                return;
            }
            const coaches = await DataOps.fetchCoachDataList(camp.campCoaches);
            setCoachData(coaches);
        };

        fetchCoachData();
    }, [camp?.campCoaches]);

    useEffect(() => {
        // Check if user is host or if they're the assigned coach
        const isHostOfCamp = userCampId === session.campId;
        const isAssignedCoach = user && session.coach &&
            (session.coach.userId === user.userId || session.coach.id === user.userId);
        setIsHost(isHostOfCamp || isAssignedCoach);
    }, [userCampId, user, session.coach]);

    const updateEditableSession = (fieldName, fieldValue) => {
        setEditableSession(
            prev => (
                {
                    ...prev,
                    [fieldName]: fieldValue,
                }
            )
        );

        setSessions(
            prev => {
                const updatedSessions = { ...prev };
                updatedSessions[session.sessionId] = {
                    ...updatedSessions[session.sessionId],
                    [fieldName]: fieldValue,
                };
                return updatedSessions;
            }
        );
    };

    const isUserRegistered = () => {
        if (user && user.upcomingSessions) {
            return user.upcomingSessions.includes(session.sessionId);
        }

        return false;
    }

    const getFormattedAddress = (address) => {
        if (!address) return '';
        // Extract city and state (typically positions -3 and -2, before zipcode and country)
        const parts = address.split(',').map(part => part.trim());
        if (parts.length >= 3) {
            return parts.slice(-3, -1).join(', '); // Get city, state (skip zipcode and country)
        } else if (parts.length >= 2) {
            return parts.slice(0, 2).join(', '); // Fallback to first 2 parts
        }
        return parts.join(', ');
    };

    const handleMapPress = () => {
        if (!session.campAddress) return;

        const encodedAddress = encodeURIComponent(session.campAddress);
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

        window.open(googleMapsUrl, '_blank');
    };

    const handleCalendarPress = () => {
        if (!session.startDate || !session.endDate || !session.startTime || !session.endTime) return;

        // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
        const formatDateTimeForCalendar = (date, time) => {
            const dateObj = new Date(`${date} ${time}`);
            return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const startDateTime = formatDateTimeForCalendar(session.startDate, session.startTime);
        const endDateTime = formatDateTimeForCalendar(session.endDate, session.endTime);

        // Create event details
        const title = encodeURIComponent(session.sessionTitle || 'Camp Session');
        const location = encodeURIComponent(session.campAddress || '');
        const details = encodeURIComponent(`Camp session: ${session.sessionTitle || 'Untitled'}`);

        // Create Google Calendar URL
        const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateTime}/${endDateTime}&location=${location}&details=${details}`;

        window.open(calendarUrl, '_blank');
    };

    const handleCoachEmailPress = () => {
        if (!editableSession.coach?.email) return;

        const coachName = `${editableSession.coach.firstName || ''} ${editableSession.coach.lastName || ''}`.trim() || 'Coach';

        // Create Gmail compose URL
        const subject = encodeURIComponent(`Inquiry about ${editableSession.title || 'Camp Session'}`);
        const body = encodeURIComponent(`Hello ${coachName},\n\nI'm interested in learning more about your camp session "${editableSession.title || 'your camp session'}" scheduled for ${editableSession.startDate} - ${editableSession.endDate}.\n\nThank you!`);

        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${editableSession.coach.email}&su=${subject}&body=${body}`;

        window.open(gmailUrl, '_blank');
    };

    const handleSessionPress = (e?: any) => {
        try {
            // If coach modal is open, swallow navigation
            if ((window as any).__coachModalOpen) {
                if (e?.stopPropagation) e.stopPropagation();
                if (e?.preventDefault) e.preventDefault();
                return;
            }
        } catch { }
        navigate(`/camps/${session.campId}`);
    };

    const [showShareModal, setShowShareModal] = useState(false);
    const [showPhotosModal, setShowPhotosModal] = useState(false);

    const getShareUrl = () => {
        const sid = session.sessionId || session.id;
        return `${window.location.origin}/camps/${session.campId}${sid ? `?sessionId=${sid}` : ''}`;
    };

    const getSessionDescription = () => {
        const type = (session.sessionType || '').toString();
        const dateRange = [session.startDate, session.endDate].filter(Boolean).join(' - ');
        return `${type} session at ${session.campName || 'SnapCamp'}${dateRange ? ` · ${dateRange}` : ''}`;
    };

    const sessionPhotos = ((editableSession.sessionImageUrls || []) as string[]).map((url: string, idx: number) => ({
        id: `${idx}`,
        uri: url,
        timestamp: new Date().toISOString(),
        author: 'Host',
    }));

    const handleAddSessionPhoto = async (dataUrl: string) => {
        try {
            const imageId = `session_${editableSession.sessionId || session.sessionId || uuidv4()}_${Date.now()}`;
            const uploadedUrl = await DataOps.uploadImage(dataUrl, imageId);
            const updated = [...((editableSession.sessionImageUrls || []) as string[]), uploadedUrl];
            updateEditableSession('sessionImageUrls', updated);
        } catch (e) {
        }
    };

    const handleDeleteSessionPhoto = async (photoId: string, uri: string) => {
        const current = ((editableSession.sessionImageUrls || []) as string[]);
        const updated = current.filter((u: string) => u !== uri);
        updateEditableSession('sessionImageUrls', updated);
    };

    const updateSessionFollowers = async () => {
        const sessionInfo = await DataOps.fetchDataFromFirebase('session', session.sessionId);
        const sessionFollowers = sessionInfo.sessionFollowers || [];

        let newSessionFollowers = new Set([...sessionFollowers]);
        if (isLiked) {
            newSessionFollowers.delete(user.userId);
        } else {
            newSessionFollowers.add(user.userId);
        }

        await DataOps.saveCustomDataToFirebase(
            'session', session.sessionId, { ...sessionInfo, sessionFollowers: [...newSessionFollowers] }, true
        );
    };

    const updateLikedSessions = () => {
        let newLikedSessions = new Set([...likedSessions]);
        if (isLiked) {
            newLikedSessions.delete(session.sessionId);
        } else {
            newLikedSessions.add(session.sessionId);
        }

        setLikedSessions(newLikedSessions);

        return newLikedSessions;
    };

    const handleLikePress = async () => {
        const newLikedSessions = await updateLikedSessions();

        await DataOps.saveCustomDataToFirebase(
            'user', user.userId,
            { ...user, likedSessions: [...newLikedSessions] }, true);

        await updateSessionFollowers();
    };



    const styles = StyleSheet.create({
        sessionCard: {
            width: WIDTH,
            borderWidth: 0.5,
            borderRadius: 20,
            borderColor: '#e8e8e8',
            marginLeft: campSessionPage ? 5 : 12,
            marginBottom: 30,
            marginRight: campSessionPage ? 8 : 10,
            zIndex: 5,
            backgroundColor: 'white',
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 4,
            },
            shadowOpacity: 0.08,
            shadowRadius: 8,
            elevation: 3,
            overflow: 'hidden',
        },
        imageContainer: {
            position: 'relative',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            overflow: 'hidden',
        },
        actionButtonsOverlay: {
            position: 'absolute',
            top: 12,
            right: 12,
            flexDirection: 'row',
            gap: 8,
        },
        actionButton: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: 20,
            padding: 8,
            zIndex: 10,
        },
        description: {
            flexDirection: 'column' as const,
            paddingHorizontal: 20,
            paddingTop: 16,
            paddingBottom: 16,
        },
        titleText: {
            fontSize: 14,
            fontWeight: '600' as const,
            marginBottom: 2,
            textAlign: 'left' as const,
            color: '#1a1a1a',
            lineHeight: 18,
        },
        subtitleText: {
            fontSize: 11,
            fontWeight: '400' as const,
            color: '#8b8b8b',
            marginBottom: 8,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
        },
        infoTextContainer: {
            flexDirection: 'row' as const,
            marginBottom: 10,
            alignItems: 'center' as const,
            paddingVertical: 6,
            paddingHorizontal: 12,
            marginHorizontal: -12,
            borderRadius: 8,
            minHeight: 36,
        },
        infoText: {
            fontSize: 13,
            fontWeight: 'normal' as const,
            marginLeft: 6,
            flex: 1,
        },
        clickableText: {
            color: 'black',
        },
        clickableTextHovered: {
            color: '#2C67D6',
        },
        hoveredMapRow: {
            backgroundColor: '#F0F7FF',
            transform: [{ scale: 1.02 }],
        },
        hoveredCalendarRow: {
            backgroundColor: '#F0F7FF',
            transform: [{ scale: 1.02 }],
        },
        hoveredEmailRow: {
            backgroundColor: '#F0F7FF',
            transform: [{ scale: 1.02 }],
        },
        divider: {
            height: 1,
            backgroundColor: '#f0f0f0',
            marginHorizontal: 0,
            marginTop: 16,
            marginBottom: 16,
        },
        priceLine: {
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            marginTop: 'auto',
            marginBottom: 8,
            paddingHorizontal: 0,
            justifyContent: 'space-between',
        },
        priceText: {
            color: '#2874A6',
            fontSize: 14,
            fontWeight: '600' as const,
        },
        textInput: {
            borderWidth: 1,
            borderColor: 'lightgray',
            borderRadius: 30,
            paddingVertical: 2,
            paddingLeft: 5,
        },
        editablePriceContainer: {
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 4,
            minHeight: 24,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
            position: 'relative',
        },
        hoveredPriceContainer: {
            backgroundColor: '#f8f9fa',
            borderWidth: 1,
            borderColor: '#dee2e6',
        },
        editIcon: {
            marginLeft: 4,
            opacity: 0.7,
        },
        editableFieldContainer: {
            paddingHorizontal: 2,
            paddingVertical: 0,
            borderRadius: 4,
            justifyContent: 'flex-start',
            flexDirection: 'row',
            alignItems: 'center',
            position: 'relative',
            borderWidth: 1,
            borderColor: 'transparent',
            flex: 1,
        },
        hoveredFieldContainer: {
            backgroundColor: '#f8f9fa',
            borderColor: '#dee2e6',
            transform: [{ scale: 1.01 }],
        },
        headerRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 12,
        },
        titleContainer: {
            flex: 1,
            marginRight: 16,
        },
        actionButtons: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: 20,
            paddingHorizontal: 4,
            paddingVertical: 2,
        },
        actionButtonSmall: {
            padding: 6,
            marginHorizontal: 2,
            borderRadius: 16,
            backgroundColor: 'transparent',
        },
        bannerContainer: {
            marginBottom: 8,
        },
        titleSection: {
            marginBottom: 10,
        },
        compactInfoSection: {
            marginBottom: 8,
        },
        compactInfoRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 8,
            paddingVertical: 2,
            paddingHorizontal: 8,
            marginHorizontal: -8,
            borderRadius: 6,
            minHeight: 22,
        },
        compactInfoText: {
            fontSize: 12,
            fontWeight: 'normal',
            marginLeft: 10,
            flex: 1,
            color: '#333',
            textAlign: 'left',
        },
        inlineFollowerStat: {
            flexDirection: 'row',
            alignItems: 'center',
            marginLeft: 8,
            flexShrink: 0,
        },
        inlineFollowerText: {
            fontSize: 11,
            color: '#333',
            marginLeft: 4,
        },
        registrationBannerContainer: {
            transform: [{ scale: 0.85 }],
            transformOrigin: 'bottom right',
        },

    });

    return (
        session && <View style={{ zIndex: 5, marginBottom: 30 }}>
            {isEditing && <Pressable style={{ justifyContent: 'center', alignItems: 'center', marginBottom: 5 }} onPress={() => updateEditableSession('deleted', true)}>
                <MaterialCommunityIcons name='minus-circle' size={30} color='red' />
            </Pressable>}

            <Pressable style={styles.sessionCard} onPress={handleSessionPress} disabled={isEditing}>
                <View style={styles.imageContainer}>
                    <CardImageContainer
                        images={(editableSession.sessionImageUrls && editableSession.sessionImageUrls.length > 0)
                            ? editableSession.sessionImageUrls
                            : ([])}
                        isSessionView
                    />

                    <View style={styles.actionButtonsOverlay}>
                        {!isEditing && (
                            <>
                                {/* Share button */}
                                <Pressable style={styles.actionButton} onPress={(e) => { e.stopPropagation(); setShowShareModal(true); }}>
                                    <MaterialCommunityIcons
                                        name="share-variant-outline"
                                        size={20}
                                        color="white"
                                    />
                                </Pressable>

                                <Pressable style={styles.actionButton} onPress={(e) => { e.stopPropagation(); handleLikePress(); }}>
                                    <MaterialCommunityIcons
                                        name={isLiked ? 'heart' : 'heart-outline'}
                                        size={20}
                                        color={isLiked ? '#FF6B6B' : 'white'}
                                    />
                                </Pressable>
                            </>
                        )}

                        {isEditing && (
                            <Pressable style={styles.actionButton} onPress={(e) => { e.stopPropagation(); setShowPhotosModal(true); }}>
                                <MaterialCommunityIcons
                                    name="camera"
                                    size={20}
                                    color="white"
                                />
                            </Pressable>
                        )}
                    </View>
                </View>

                <View style={styles.description}>
                    <View style={styles.bannerContainer}>
                        <SessionTypeBanner
                            sessionType={isEditing ? editableSession.sessionType : session.sessionType}
                            updateValue={updateEditableSession}
                            isEditing={isEditing}
                            onEditRequest={() => setShowSessionTypeModal(true)}
                        />
                    </View>

                    <View style={styles.titleSection}>
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowTitleModal(true)}
                                    onHoverIn={() => setHoveredField('title')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'title' && styles.hoveredFieldContainer
                                    ]}
                                >
                                    <Text style={[styles.titleText, { color: editableSession.title ? '#1a1a1a' : 'grey' }]}>
                                        {editableSession.title || 'Session name'}
                                    </Text>
                                    {hoveredField === 'title' && (
                                        <MaterialCommunityIcons
                                            name="pencil"
                                            size={12}
                                            color="#666"
                                            style={styles.editIcon}
                                        />
                                    )}
                                </Pressable>
                                : <Text style={styles.titleText}>{editableSession.title}</Text>
                        }
                        <Text style={styles.subtitleText}>{session.campName}</Text>
                    </View>

                    <View style={styles.compactInfoSection}>
                        <Pressable
                            style={[
                                styles.compactInfoRow,
                                isMapRowHovered && styles.hoveredMapRow
                            ]}
                            onPress={handleMapPress}
                            onHoverIn={() => setIsMapRowHovered(true)}
                            onHoverOut={() => setIsMapRowHovered(false)}
                        >
                            <MaterialCommunityIcons name='map-marker' size={16} color={isMapRowHovered ? '#2C67D6' : '#666'} />
                            <Text style={[
                                styles.compactInfoText,
                                styles.clickableText,
                                isMapRowHovered && styles.clickableTextHovered
                            ]} numberOfLines={1}>{getFormattedAddress(session.campAddress)}</Text>
                        </Pressable>

                        <Pressable
                            style={[
                                styles.compactInfoRow,
                                isCalendarRowHovered && styles.hoveredCalendarRow
                            ]}
                            onPress={handleCalendarPress}
                            onHoverIn={() => setIsCalendarRowHovered(true)}
                            onHoverOut={() => setIsCalendarRowHovered(false)}
                            disabled={isEditing}
                        >
                            <MaterialCommunityIcons name='calendar' size={16} color={isCalendarRowHovered ? '#2C67D6' : '#666'} />
                            <View style={{ flex: 1 }}>
                                {
                                    isEditing
                                        ? <View style={{ flexDirection: 'column' }}>
                                            <Pressable
                                                onPress={() => setShowDateRangeModal(true)}
                                                onHoverIn={() => setHoveredField('dateRange')}
                                                onHoverOut={() => setHoveredField(null)}
                                                style={[
                                                    styles.editableFieldContainer,
                                                    hoveredField === 'dateRange' && styles.hoveredFieldContainer,
                                                    { marginBottom: 2 }
                                                ]}
                                            >
                                                <Text style={[styles.compactInfoText, { color: (editableSession.startDate && editableSession.endDate) ? '#333' : 'grey' }]} numberOfLines={1}>
                                                    {(editableSession.startDate && editableSession.endDate) ?
                                                        [editableSession.startDate, editableSession.endDate].join(' - ') + (editableSession.daysOfWeek && editableSession.daysOfWeek.length > 0 && editableSession.daysOfWeek.length < 7 ? ` ${SessionUtils.formatDaysAbbrev(editableSession.daysOfWeek, editableSession.daysMask)}` : '') :
                                                        'Set dates'}
                                                </Text>
                                            </Pressable>
                                            <Pressable
                                                onPress={() => setShowTimeRangeModal(true)}
                                                onHoverIn={() => setHoveredField('timeRange')}
                                                onHoverOut={() => setHoveredField(null)}
                                                style={[
                                                    styles.editableFieldContainer,
                                                    hoveredField === 'timeRange' && styles.hoveredFieldContainer
                                                ]}
                                            >
                                                <Text style={[styles.compactInfoText, { color: (editableSession.startTime && editableSession.endTime) ? '#666' : 'lightgrey', fontSize: 11 }]} numberOfLines={1}>
                                                    {(editableSession.startTime && editableSession.endTime) ?
                                                        [editableSession.startTime, editableSession.endTime].join(' - ') :
                                                        'Set times'
                                                    }
                                                </Text>
                                            </Pressable>
                                        </View>
                                        : <View>
                                            <Text style={[
                                                styles.compactInfoText,
                                                styles.clickableText,
                                                isCalendarRowHovered && styles.clickableTextHovered
                                            ]} numberOfLines={1}>
                                                {[editableSession.startDate, editableSession.endDate].join(' - ')}
                                                {editableSession.daysOfWeek && editableSession.daysOfWeek.length > 0 && editableSession.daysOfWeek.length < 7
                                                    ? ` ${SessionUtils.formatDaysAbbrev(editableSession.daysOfWeek, editableSession.daysMask)}`
                                                    : ''}
                                            </Text>
                                            <Text style={[
                                                styles.compactInfoText,
                                                styles.clickableText,
                                                isCalendarRowHovered && styles.clickableTextHovered,
                                                { fontSize: 11, color: isCalendarRowHovered ? '#2C67D6' : '#666', marginTop: 2 }
                                            ]} numberOfLines={1}>{[editableSession.startTime, editableSession.endTime].join(' - ')}</Text>
                                        </View>
                                }
                            </View>
                        </Pressable>
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name='account-multiple-outline' size={16} color='#666' />
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowAgeRangeModal(true)}
                                    onHoverIn={() => setHoveredField('ageRange')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'ageRange' && styles.hoveredFieldContainer,
                                        { flex: 1 }
                                    ]}
                                >
                                    <Text style={[styles.compactInfoText, { color: (editableSession.startAge && editableSession.endAge) ? '#333' : 'grey' }]} numberOfLines={1}>
                                        {(editableSession.startAge && editableSession.endAge) ?
                                            `Ages ${[editableSession.startAge, editableSession.endAge].join('-')}` :
                                            'Set age range'
                                        }
                                    </Text>
                                </Pressable>
                                : <Text style={styles.compactInfoText} numberOfLines={1}>Ages {[editableSession.startAge, editableSession.endAge].join('-')}</Text>
                        }
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name='account-group-outline' size={16} color='#666' />
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowClassSizeModal(true)}
                                    onHoverIn={() => setHoveredField('classSize')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'classSize' && styles.hoveredFieldContainer,
                                        { flex: 1 }
                                    ]}
                                >
                                    <Text style={[styles.compactInfoText, { color: editableSession.capacity ? '#333' : 'grey' }]} numberOfLines={1}>
                                        {editableSession.capacity ?
                                            `Class size ${editableSession.capacity}` :
                                            'Set class size'
                                        }
                                    </Text>
                                </Pressable>
                                : <Text style={styles.compactInfoText} numberOfLines={1}>Class size {editableSession.capacity}</Text>
                        }
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name='account-tie-outline' size={16} color='#666' />
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowInstructorModal(true)}
                                    onHoverIn={() => setHoveredField('coach')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'coach' && styles.hoveredFieldContainer,
                                        { flex: 1 }
                                    ]}
                                >
                                    <Text style={[styles.compactInfoText, { color: editableSession.coach ? '#333' : 'grey' }]} numberOfLines={1}>
                                        {editableSession.coach ?
                                            `${editableSession.coach.firstName || ''} ${editableSession.coach.lastName || ''}`.trim() || 'Coach' :
                                            'Set coach'
                                        }
                                    </Text>
                                </Pressable>
                                : <Pressable
                                    onPress={editableSession.coach?.email ? handleCoachEmailPress : undefined}
                                    onHoverIn={() => editableSession.coach?.email && setIsInstructorRowHovered(true)}
                                    onHoverOut={() => setIsInstructorRowHovered(false)}
                                    style={[{ flex: 1 }, editableSession.coach?.email && isInstructorRowHovered && styles.hoveredEmailRow]}
                                >
                                    <Text style={[
                                        styles.compactInfoText,
                                        { color: editableSession.coach ? '#333' : 'grey' },
                                        editableSession.coach?.email && styles.clickableText,
                                        editableSession.coach?.email && isInstructorRowHovered && styles.clickableTextHovered
                                    ]} numberOfLines={1}>
                                        {editableSession.coach ?
                                            `${editableSession.coach.firstName || ''} ${editableSession.coach.lastName || ''}`.trim() || 'Coach' :
                                            'No coach'
                                        }
                                    </Text>
                                </Pressable>
                        }
                        <View style={styles.inlineFollowerStat}>
                            <MaterialCommunityIcons name='heart-outline' size={15} color='#4CAF50' />
                            <Text style={styles.inlineFollowerText}>
                                {sessionFollowersCount} {sessionFollowersCount === 1 ? 'follower' : 'followers'}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name={editableSession.mealsIncluded ? 'food-outline' : 'food-off-outline'} size={16} color='#666' />
                        <Text style={[styles.compactInfoText, { flex: 1 }]} numberOfLines={1}>
                            {editableSession.mealsIncluded ? 'Meals included' : 'No meals'}
                        </Text>
                        {
                            isEditing && <Switch
                                style={{ marginLeft: 10 }}
                                value={editableSession.mealsIncluded}
                                onValueChange={() => updateEditableSession('mealsIncluded', !editableSession.mealsIncluded)}
                            />
                        }
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name='calendar-remove-outline' size={16} color='#666' />
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowCancellationModal(true)}
                                    onHoverIn={() => setHoveredField('cancellation')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'cancellation' && styles.hoveredFieldContainer,
                                        { flex: 1 }
                                    ]}
                                >
                                    <Text style={[styles.compactInfoText, { color: editableSession.cancellationPolicy ? '#333' : 'grey' }]} numberOfLines={1}>
                                        {editableSession.cancellationPolicy ?
                                            `${editableSession.cancellationPolicy} cancellation` :
                                            'Set cancellation policy'
                                        }
                                    </Text>
                                </Pressable>
                                : <Text style={styles.compactInfoText} numberOfLines={1}>{editableSession.cancellationPolicy || '7-day'} cancellation</Text>
                        }
                    </View>

                    <View style={styles.compactInfoRow}>
                        <MaterialCommunityIcons name='file-sign' size={16} color={editableSession.waiverRequired ? '#1D4ED8' : '#666'} />
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowWaiverModal(true)}
                                    onHoverIn={() => setHoveredField('waiver')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editableFieldContainer,
                                        hoveredField === 'waiver' && styles.hoveredFieldContainer,
                                        { flex: 1 }
                                    ]}
                                >
                                    <Text style={[styles.compactInfoText, { color: editableSession.waiverRequired ? '#1D4ED8' : '#999' }]} numberOfLines={1}>
                                        {editableSession.waiverRequired
                                            ? (editableSession.waiverTitle ? `Waiver: ${editableSession.waiverTitle}` : 'Waiver required · Upload document')
                                            : 'No waiver required'}
                                    </Text>
                                    {hoveredField === 'waiver' && (
                                        <MaterialCommunityIcons name="pencil" size={12} color="#666" style={styles.editIcon} />
                                    )}
                                </Pressable>
                                : editableSession.waiverRequired
                                    ? <Pressable
                                        onPress={() => editableSession.waiverUrl && window.open(editableSession.waiverUrl, '_blank')}
                                        onHoverIn={() => setIsWaiverRowHovered(true)}
                                        onHoverOut={() => setIsWaiverRowHovered(false)}
                                        style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                                    >
                                        <Text style={[styles.compactInfoText, { color: isWaiverRowHovered ? '#1E40AF' : '#1D4ED8', textDecorationLine: editableSession.waiverUrl ? 'underline' : 'none' }]} numberOfLines={1}>
                                            Waiver required{editableSession.waiverUrl ? ' · View' : ''}
                                        </Text>
                                    </Pressable>
                                    : <Text style={[styles.compactInfoText, { color: '#999', flex: 1 }]} numberOfLines={1}>No waiver</Text>
                        }
                    </View>

                    <View style={styles.priceLine}>
                        {
                            isEditing
                                ? <Pressable
                                    onPress={() => setShowPriceModal(true)}
                                    onHoverIn={() => setHoveredField('price')}
                                    onHoverOut={() => setHoveredField(null)}
                                    style={[
                                        styles.editablePriceContainer,
                                        hoveredField === 'price' && styles.hoveredPriceContainer
                                    ]}
                                >
                                    <Text style={[styles.priceText, { color: editableSession.price ? '#2874A6' : 'grey' }]}>
                                        {editableSession.price === '0' ? 'FREE' : editableSession.price ? '$' + editableSession.price : 'Set price'}
                                    </Text>
                                    {hoveredField === 'price' && (
                                        <MaterialCommunityIcons
                                            name="pencil"
                                            size={12}
                                            color="#666"
                                            style={styles.editIcon}
                                        />
                                    )}
                                </Pressable>
                                : <Text style={styles.priceText}>{editableSession.price === '0' ? 'FREE' : '$' + editableSession.price}</Text>
                        }

                        <View style={{ flex: 1 }} />

                        <View style={styles.registrationBannerContainer}>
                            <RegistrationBanner
                                sessionId={session.sessionId}
                                session={session}
                                isHost={isHost}
                                isEditing={isEditing}
                                updateValue={updateEditableSession}
                                registeredUser={isUserRegistered()}
                            />
                        </View>
                    </View>
                </View>
            </Pressable>

            <ShareModal
                visible={showShareModal}
                onClose={() => setShowShareModal(false)}
                title={editableSession.title || session.title || 'Camp Session'}
                description={getSessionDescription()}
                imageUrl={(editableSession.sessionImageUrls && editableSession.sessionImageUrls[0]) || (session.imageUrls && session.imageUrls[0]) || undefined}
                shareUrl={getShareUrl()}
            />

            {isEditing && (
                <PhotosModal
                    visible={showPhotosModal}
                    onClose={() => setShowPhotosModal(false)}
                    title={editableSession.title || session.title || 'Session'}
                    photos={sessionPhotos}
                    onAddPhoto={handleAddSessionPhoto}
                    canAdd={true}
                    onDeletePhoto={handleDeleteSessionPhoto}
                />
            )}

            {/* Edit Modals */}
            <PriceEditModal
                visible={showPriceModal}
                onClose={() => setShowPriceModal(false)}
                currentPrice={editableSession.price}
                onSave={(newPrice) => {
                    updateEditableSession('price', newPrice);
                }}
                sessionTitle={editableSession.title || 'Edit Price'}
            />

            <TitleEditModal
                visible={showTitleModal}
                onClose={() => setShowTitleModal(false)}
                currentTitle={editableSession.title}
                onSave={(newTitle) => {
                    updateEditableSession('title', newTitle);
                }}
                sessionTitle="Edit Session Title"
            />

            <ClassSizeEditModal
                visible={showClassSizeModal}
                onClose={() => setShowClassSizeModal(false)}
                currentCapacity={editableSession.capacity}
                onSave={(newCapacity) => {
                    updateEditableSession('capacity', newCapacity);
                }}
                sessionTitle={editableSession.title || 'Edit Class Size'}
            />

            <AgeRangeEditModal
                visible={showAgeRangeModal}
                onClose={() => setShowAgeRangeModal(false)}
                currentStartAge={editableSession.startAge}
                currentEndAge={editableSession.endAge}
                onSave={(startAge, endAge) => {
                    updateEditableSession('startAge', startAge);
                    updateEditableSession('endAge', endAge);
                }}
                sessionTitle={editableSession.title || 'Edit Age Range'}
            />

            <DateRangeEditModal
                visible={showDateRangeModal}
                onClose={() => setShowDateRangeModal(false)}
                currentStartDate={editableSession.startDate}
                currentEndDate={editableSession.endDate}
                currentDaysOfWeek={editableSession.daysOfWeek}
                onSave={(startDate, endDate, daysOfWeek) => {
                    updateEditableSession('startDate', startDate);
                    updateEditableSession('endDate', endDate);
                    try {
                        const s = new Date(startDate);
                        updateEditableSession('startAt', s);

                        const e = new Date(endDate);
                        updateEditableSession('endAt', e);
                    } catch { }
                    updateEditableSession('daysOfWeek', daysOfWeek);
                    updateEditableSession('daysMask', SessionUtils.daysOfWeekToMask(daysOfWeek));
                }}
                sessionTitle={editableSession.title || 'Edit Date Range'}
            />

            <TimeRangeEditModal
                visible={showTimeRangeModal}
                onClose={() => setShowTimeRangeModal(false)}
                currentStartTime={editableSession.startTime}
                currentEndTime={editableSession.endTime}
                onSave={(startTime, endTime) => {
                    updateEditableSession('startTime', startTime);
                    updateEditableSession('endTime', endTime);
                }}
                sessionTitle={editableSession.title || 'Edit Time Range'}
            />

            <CancellationPolicyEditModal
                visible={showCancellationModal}
                onClose={() => setShowCancellationModal(false)}
                currentPolicy={editableSession.cancellationPolicy}
                onSave={(newPolicy) => {
                    updateEditableSession('cancellationPolicy', newPolicy);
                }}
                sessionTitle={editableSession.title || 'Edit Cancellation Policy'}
            />

            <WaiverEditModal
                visible={showWaiverModal}
                onClose={() => setShowWaiverModal(false)}
                sessionId={editableSession.sessionId || session.sessionId || ''}
                currentWaiverRequired={!!editableSession.waiverRequired}
                currentWaiverTitle={editableSession.waiverTitle}
                currentWaiverUrl={editableSession.waiverUrl}
                onSave={(required, title, url) => {
                    updateEditableSession('waiverRequired', required);
                    updateEditableSession('waiverTitle', title);
                    updateEditableSession('waiverUrl', url);
                }}
                sessionTitle={editableSession.title || 'Session Waiver'}
            />

            <SessionTypeEditModal
                visible={showSessionTypeModal}
                onClose={() => setShowSessionTypeModal(false)}
                currentType={editableSession.sessionType}
                onSave={(newType) => updateEditableSession('sessionType', newType)}
                sessionTitle={editableSession.title || 'Edit Session Type'}
            />

            <CoachEditModal
                visible={showInstructorModal}
                onClose={() => setShowInstructorModal(false)}
                currentCoach={editableSession.coach}
                onSave={async (newCoach) => {
                    const previousCoach = editableSession.coach;

                    // Store minimal coach object
                    const coachId = newCoach ? (newCoach.userId || newCoach.id) : null;
                    const coachName = newCoach ? `${newCoach.firstName || ''} ${newCoach.lastName || ''}`.trim() || 'Coach' : null;
                    const minimalCoach = newCoach ? {
                        userId: coachId,
                        name: coachName,
                        email: newCoach.email || null,
                        phone: newCoach.phone || null,
                        assignedAt: new Date(),
                    } : null;

                    updateEditableSession('coach', minimalCoach);

                    if (minimalCoach && (!previousCoach || previousCoach.userId !== minimalCoach.userId)) {
                        // Track assignment in coach document
                        try {
                            if (camp?.campId) {
                                await DataOps.addSessionAssignmentToCoach(minimalCoach.userId, camp.campId, editableSession.sessionId);
                            }
                        } catch (e) { }
                        try {
                            await NotificationOps.createNotificationForAnyAccount(
                                minimalCoach.userId,
                                `You have been assigned as coach for session "${editableSession?.title || 'Untitled Session'}" at ${camp?.campName || 'a camp'}.`,
                                'session_assigned',
                                editableSession.sessionId,
                                camp?.campId,
                                {
                                    sessionTitle: editableSession?.title,
                                    campName: camp?.campName,
                                    startDate: editableSession?.startDate,
                                    endDate: editableSession?.endDate
                                }
                            );
                        } catch (error) {
                            console.error('Error sending notification to coach:', error);
                        }
                    }

                    // Send notification to previously assigned coach if they were removed/replaced
                    if (previousCoach && (!minimalCoach || previousCoach.userId !== minimalCoach.userId)) {
                        // Remove assignment from previous coach document
                        try {
                            if (camp?.campId) {
                                await DataOps.removeSessionAssignmentFromCoach(previousCoach.userId, camp.campId, editableSession.sessionId);
                            }
                        } catch (e) { }
                        try {
                            await NotificationOps.createNotificationForAnyAccount(
                                previousCoach.userId,
                                `You have been unassigned from session "${editableSession?.title || 'Untitled Session'}" at ${camp?.campName || 'a camp'}.`,
                                'session_unassigned',
                                editableSession.sessionId,
                                camp?.campId,
                                {
                                    sessionTitle: editableSession?.title,
                                    campName: camp?.campName,
                                    startDate: editableSession?.startDate,
                                    endDate: editableSession?.endDate
                                }
                            );
                        } catch (error) {
                            console.error('Error sending unassignment notification to previous coach:', error);
                        }
                    }
                }}
                campCoaches={coachData}
                sessionTitle={editableSession.title || 'Set Coach'}
            />
        </View>
    );
};

export default SessionCard;